// Privacy-first incident copy generator. All transformation stays in-browser;
// there are no fetches, storage calls, analytics payloads, or URL parameters.
// Every fixed sentence is a message in the page's language; the visitor's
// own sentences go between them.
import { midSentence, t } from "./_i18n.js";

function clean(value, fallback) {
  const text = value.trim().replace(/\s+/g, " ");
  return text || fallback;
}

function sentence(value) {
  const text = clean(value, "");
  if (!text) return "";
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

// Service, impact, action and guidance for each preset button.
const PRESETS = {
  website: ["inc-msg-preset-website-service", "inc-msg-preset-website-impact", "inc-msg-preset-website-action", "inc-msg-no-action"],
  api: ["inc-msg-preset-api-service", "inc-msg-preset-api-impact", "inc-msg-preset-api-action", "inc-msg-preset-api-guidance"],
  performance: ["inc-msg-preset-performance-service", "inc-msg-preset-performance-impact", "inc-msg-preset-performance-action", "inc-msg-no-action"],
  login: ["inc-msg-preset-login-service", "inc-msg-preset-login-impact", "inc-msg-preset-login-action", "inc-msg-preset-login-guidance"],
  payments: ["inc-msg-preset-payments-service", "inc-msg-preset-payments-impact", "inc-msg-preset-payments-action", "inc-msg-preset-payments-guidance"],
  regional: ["inc-msg-preset-regional-service", "inc-msg-preset-regional-impact", "inc-msg-preset-regional-action", "inc-msg-preset-regional-guidance"],
};

function incidentCopy(state) {
  const service = clean(state.service, t("inc-msg-fallback-service"));
  const impact = sentence(clean(state.impact, t("inc-msg-fallback-impact")));
  const action = sentence(clean(state.action, t("inc-msg-fallback-action")));
  const next = midSentence(clean(state.next, t("inc-msg-fallback-next")));
  const again = t("inc-msg-again", { next });
  const guidance = state.guidance.trim() ? ` ${sentence(state.guidance)}` : "";

  const variants = {
    investigating: [t("inc-msg-title-investigating", { service }), `${impact}${guidance} ${action} ${again}`],
    identified: [t("inc-msg-title-identified", { service }), `${impact}${guidance} ${t("inc-msg-identified")} ${action} ${again}`],
    monitoring: [t("inc-msg-title-monitoring", { service }), `${impact}${guidance} ${t("inc-msg-mitigation")} ${action} ${t("inc-msg-monitoring", { next })}`],
    resolved: [t("inc-msg-title-resolved", { service }), `${impact} ${t("inc-msg-resolved")}${guidance} ${t("inc-msg-reviewing")}`],
  };
  return variants[state.phase];
}

function maintenanceCopy(state) {
  const service = clean(state.service, t("inc-msg-fallback-service"));
  // Inside a sentence the service is an object, which some languages word
  // apart from the title's: "den Dienst".
  const object = clean(state.service, t("inc-msg-fallback-service-object"));
  const impact = sentence(clean(state.impact, t("inc-msg-fallback-maintenance-impact")));
  const action = sentence(clean(state.action, t("inc-msg-fallback-maintenance-action")));
  const next = midSentence(clean(state.next, t("inc-msg-fallback-maintenance-next")));
  const again = t("inc-msg-post-again", { next });
  const guidance = state.guidance.trim() ? ` ${sentence(state.guidance)}` : "";
  const variants = {
    scheduled: [t("inc-msg-title-scheduled", { service }), `${t("inc-msg-scheduled", { service: object })} ${impact}${guidance} ${action} ${again}`],
    "in-progress": [t("inc-msg-title-in-progress", { service }), `${t("inc-msg-in-progress", { service: object })} ${impact}${guidance} ${action} ${again}`],
    completed: [t("inc-msg-title-completed", { service }), `${t("inc-msg-completed", { service: object })}${guidance}`],
  };
  return variants[state.maintenancePhase];
}

function isTerminal(state) {
  return (state.type === "incident" && state.phase === "resolved") ||
    (state.type === "maintenance" && state.maintenancePhase === "completed");
}

function emailWrap(state, message) {
  const service = clean(state.service, t("inc-msg-email-service"));
  const about = state.type === "maintenance"
    ? t("inc-msg-email-maintenance", { service })
    : t("inc-msg-email-incident", { service });
  const outro = isTerminal(state) ? t("inc-msg-email-thanks") : t("inc-msg-email-ongoing");
  return `${t("inc-msg-greeting")}\n\n${about} ${message}\n\n${outro}`;
}

function init() {
  const fields = {
    service: document.getElementById("incident-service"),
    impact: document.getElementById("incident-impact"),
    action: document.getElementById("incident-action"),
    guidance: document.getElementById("incident-guidance"),
    next: document.getElementById("incident-next"),
    timezone: document.getElementById("incident-timezone"),
  };
  const title = document.getElementById("incident-output-title");
  const body = document.getElementById("incident-output-body");
  const time = document.getElementById("incident-output-time");
  const copy = document.getElementById("incident-copy");
  const copyStatus = document.getElementById("incident-copy-status");
  const incidentPhases = document.getElementById("incident-phases");
  const maintenancePhases = document.getElementById("maintenance-phases");
  const presetsField = document.getElementById("incident-presets-field");
  if (!title || !body || !time || !copy || Object.values(fields).some((node) => !node)) return;

  const state = { type: "incident", phase: "investigating", maintenancePhase: "scheduled", format: "status" };
  let copiedTimer;

  function render() {
    for (const [key, node] of Object.entries(fields)) state[key] = node.value;
    const output = state.type === "incident" ? incidentCopy(state) : maintenanceCopy(state);
    title.textContent = output[0];
    body.textContent = state.format === "email" ? emailWrap(state, output[1]) : output[1];
    time.hidden = isTerminal(state);
    const nextFallback = t(state.type === "maintenance" ? "inc-msg-fallback-maintenance-next" : "inc-msg-fallback-next");
    time.textContent = t("inc-msg-next-line", { next: clean(state.next, nextFallback), timezone: state.timezone });
    copy.classList.remove("is-copied");
    renderQuality();
  }

  function renderQuality() {
    const terminal = isTerminal(state);
    const checks = {
      impact: state.impact.trim().length >= 12,
      action: terminal || state.action.trim().length >= 12,
      next: terminal || state.next.trim().length >= 5,
      guidance: state.guidance.trim().length >= 5,
    };
    let score = 0;
    for (const [key, pass] of Object.entries(checks)) {
      const item = document.querySelector(`[data-check="${key}"]`);
      if (!item) continue;
      item.classList.toggle("is-pass", pass);
      item.classList.toggle("is-advice", !pass);
      item.querySelector("span").textContent = pass ? "✓" : "+";
      if (pass) score += 1;
    }
    document.getElementById("quality-score").textContent = `${score}/4`;
    document.getElementById("quality-bar").style.setProperty("--quality", `${score * 25}%`);
  }

  for (const group of document.querySelectorAll("[data-segment]")) {
    group.addEventListener("click", (event) => {
      const button = event.target.closest("button[data-value]");
      if (!button) return;
      state[group.dataset.segment] = button.dataset.value;
      for (const peer of group.querySelectorAll("button")) {
        const on = peer === button;
        peer.classList.toggle("is-active", on);
        peer.setAttribute("aria-pressed", String(on));
      }
      if (group.dataset.segment === "type") {
        const maintenance = state.type === "maintenance";
        incidentPhases.hidden = maintenance;
        maintenancePhases.hidden = !maintenance;
        presetsField.hidden = maintenance;
      }
      render();
    });
  }

  for (const node of Object.values(fields)) node.addEventListener("input", render);

  document.querySelector(".incident-presets").addEventListener("click", (event) => {
    const button = event.target.closest("button[data-preset]");
    if (!button) return;
    const preset = PRESETS[button.dataset.preset].map((id) => t(id));
    [fields.service.value, fields.impact.value, fields.action.value, fields.guidance.value] = preset;
    for (const peer of document.querySelectorAll(".incident-presets button")) peer.classList.toggle("is-active", peer === button);
    render();
    fields.service.focus();
  });

  copy.addEventListener("click", async () => {
    const message = `${title.textContent}\n\n${body.textContent}${time.hidden ? "" : `\n\n${time.textContent}`}`;
    try {
      await navigator.clipboard.writeText(message);
    } catch (_) {
      const area = document.createElement("textarea");
      area.value = message;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      area.remove();
    }
    clearTimeout(copiedTimer);
    copy.classList.add("is-copied");
    if (copyStatus) copyStatus.textContent = t("inc-msg-copied");
    copiedTimer = setTimeout(() => {
      copy.classList.remove("is-copied");
      if (copyStatus) copyStatus.textContent = "";
    }, 1800);
  });

  render();
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
else init();
